// Boomin' — Hub (shop) detail screen (/h/:id; legacy /public/hubs/:id redirects here)
//
// Redesigned to match the rest of the redesign suite (DetailScreen, TopScreen).
// Replaces the legacy Pages/Public/HubPage.tsx tailwind layout.

import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  BRAND,
  BRAND_SOFT,
  PictX,
  PictName,
  AppHeader,
  AppFooter,
  useBoominFx,
} from "./shared";
import { GOOGLE_MAPS_API_KEY } from "../../Constants";
import {
  GOOGLE_MAPS_LOADER_ID,
  GOOGLE_MAPS_LIBRARIES,
  fetchHub,
  couponTag,
  formatExpiry,
} from "./api";
import { useParams } from "react-router-dom";
import { Coupon, Hub } from "../../Types/App";

const brand = BRAND;
const brandSoft = BRAND_SOFT;

const fullAddress = (h: Hub): string => {
  const parts = [h.state, h.city, h.address_line_1, h.address_line_2].filter(Boolean);
  return parts.join("");
};

export const HubScreen: React.FC = () => {
  useBoominFx();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [hub, setHub] = useState<Hub | null>(null);
  const [coupons, setCoupons] = useState<Coupon[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchHub(id);
      setHub(res.hub);
      setCoupons(res.coupons || []);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "店舗が見つかりませんでした");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const breadcrumb = hub ? buildBreadcrumb(hub) : null;
  const heroImg = hub?.image || "";

  return (
    <>
      <Helmet>
        {hub && (
          <title>{hub.name}｜ローカルクーポンのBoomin'（ブーミン）</title>
        )}
        {hub && (
          <meta
            name="description"
            content={`${fullAddress(hub)}の${hub.name}で使えるクーポン一覧。Boomin'（ブーミン）`.slice(
              0,
              140
            )}
          />
        )}
        {breadcrumb && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumb.jsonLd)}
          </script>
        )}
      </Helmet>
      <AppHeader />
      <div
        style={{
          background: "#FFFBF7",
          minHeight: "100vh",
          paddingBottom: 40,
          position: "relative",
        }}
      >
        {breadcrumb && <Breadcrumb crumbs={breadcrumb.visible} />}

        {/* Shop info — the merchant's uploaded image is a near-square logo,
            so we present it as a square icon on the left rather than stretching
            it into a cover hero. */}
        {hub && (
          <div style={{ padding: "20px 20px 0", position: "relative", zIndex: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 20,
                  overflow: "hidden",
                  background: brandSoft,
                  border: "1px solid #F0E6DC",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {heroImg ? (
                  <img
                    src={heroImg}
                    alt={hub.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <PictX name="shop" size={44} stroke={brand} strokeWidth={1.4} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 11,
                    color: "#777",
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      background: brandSoft,
                      color: brand,
                      fontWeight: 700,
                      padding: "3px 9px",
                      borderRadius: 999,
                      fontSize: 10,
                    }}
                  >
                    店舗
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                    <PictX name="pin" size={12} stroke="#777" strokeWidth={1.8} />
                    {hub.state}
                    {hub.city ? `・${hub.city}` : ""}
                  </span>
                </div>
                <h1
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    margin: 0,
                    letterSpacing: "0.01em",
                  }}
                >
                  {hub.name}
                </h1>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              margin: "24px 20px 0",
              padding: "16px 18px",
              background: "#fff",
              border: "1px solid #FFDAD0",
              borderRadius: 16,
              color: "#A6321E",
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            {error}
          </div>
        )}

        {/* Coupons */}
        <div style={{ padding: "24px 20px 0" }}>
          <SectionHeader icon="ticket" title="クーポン一覧" />
          <div
            style={{
              marginTop: 12,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {coupons.map((c) => (
              <HubCouponCard key={c.id} coupon={c} />
            ))}
            {!loading && !error && coupons.length === 0 && (
              <div
                style={{
                  padding: "28px 20px",
                  background: "#fff",
                  border: "1px dashed #F0E6DC",
                  borderRadius: 16,
                  textAlign: "center",
                  color: "#777",
                  fontSize: 13,
                  lineHeight: 1.7,
                }}
              >
                現在ご利用いただけるクーポンはありません 🍊
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        {hub && hub.latitude && hub.longitude && (
          <div style={{ padding: "24px 20px 0" }}>
            <SectionHeader icon="map" title="お店の場所" />
            <div
              style={{
                marginTop: 12,
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid #F0E6DC",
                background: "#fff",
              }}
            >
              <ShopMap lat={hub.latitude} lng={hub.longitude} name={hub.name} />
              <div
                style={{
                  padding: "12px 14px",
                  fontSize: 12,
                  color: "#444",
                  lineHeight: 1.7,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span>{fullAddress(hub)}</span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${hub.latitude},${hub.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: brandSoft,
                      color: brand,
                      border: 0,
                      fontWeight: 700,
                      fontSize: 11,
                      padding: "7px 12px",
                      borderRadius: 999,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      textDecoration: "none",
                    }}
                  >
                    地図で見る →
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <AppFooter />
      </div>
    </>
  );
};

type Crumb = { name: string; href?: string };

const buildBreadcrumb = (
  hub: Hub
): { visible: Crumb[]; jsonLd: object } => {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://boomin.jp";
  const visible: Crumb[] = [{ name: "ホーム", href: "/" }];
  if (hub.state)
    visible.push({ name: hub.state, href: `/area/${encodeURIComponent(hub.state)}` });
  if (hub.city)
    visible.push({ name: hub.city, href: `/area/${encodeURIComponent(hub.city)}` });
  if (hub.address_line_1)
    visible.push({
      name: hub.address_line_1,
      href: `/area/${encodeURIComponent(hub.address_line_1)}`,
    });
  if (hub.name) visible.push({ name: hub.name });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: visible.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      ...(c.href ? { item: `${origin}${c.href}` } : {}),
    })),
  };
  return { visible, jsonLd };
};

const Breadcrumb: React.FC<{ crumbs: Crumb[] }> = ({ crumbs }) => (
  <nav
    aria-label="パンくずリスト"
    style={{
      padding: "10px 16px 6px",
      fontSize: 11,
      color: "#777",
      overflowX: "auto",
      whiteSpace: "nowrap",
      WebkitOverflowScrolling: "touch",
    }}
  >
    {crumbs.map((c, i) => {
      const last = i === crumbs.length - 1;
      return (
        <React.Fragment key={`${c.name}-${i}`}>
          {c.href && !last ? (
            <a
              href={c.href}
              style={{ color: "#777", textDecoration: "none", fontWeight: 600 }}
            >
              {c.name}
            </a>
          ) : (
            <span
              style={{
                color: last ? "#1A1A1A" : "#777",
                fontWeight: last ? 700 : 600,
              }}
              aria-current={last ? "page" : undefined}
            >
              {c.name}
            </span>
          )}
          {!last && <span style={{ margin: "0 6px", color: "#bbb" }}>›</span>}
        </React.Fragment>
      );
    })}
  </nav>
);

// Coupon row — mirrors TopScreen's FeaturedCard so the hub page lists coupons
// in the same visual language and links through to /c/:id.
const HubCouponCard: React.FC<{ coupon: Coupon }> = ({ coupon }) => {
  const tag = couponTag(coupon.period_end);
  const expiry = formatExpiry(coupon.period_end);
  return (
    <a
      href={`/c/${coupon.id}`}
      style={{
        display: "block",
        background: "#fff",
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid #F0E6DC",
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <div
        style={{
          position: "relative",
          height: 160,
          overflow: "hidden",
          background: brandSoft,
        }}
      >
        {coupon.image ? (
          <img
            src={coupon.image}
            alt={coupon.title}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PictX name="ticket" size={56} stroke={brand} strokeWidth={1.4} />
          </div>
        )}
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            background: brand,
            color: "#fff",
            fontSize: 11,
            fontWeight: 800,
            padding: "5px 10px",
            borderRadius: 999,
            boxShadow: `0 2px 8px ${brand}66`,
          }}
        >
          {tag}
        </div>
      </div>
      <div style={{ padding: "12px 16px 14px" }}>
        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.3, marginBottom: 8 }}>
          {coupon.title}
        </div>
        {expiry && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: "#777",
            }}
          >
            <PictX name="clock" size={13} stroke="#777" strokeWidth={1.8} />
            {expiry}
          </div>
        )}
      </div>
    </a>
  );
};

const SectionHeader: React.FC<{ icon: PictName; title: string }> = ({ icon, title }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `${brand}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PictX name={icon} size={16} stroke={brand} strokeWidth={1.8} />
    </div>
    <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, letterSpacing: "0.02em" }}>
      {title}
    </h2>
  </div>
);

const ShopMap: React.FC<{ lat: number; lng: number; name: string }> = ({ lat, lng, name }) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  if (!GOOGLE_MAPS_API_KEY || !isLoaded) {
    return <div style={{ height: 180, background: "#F2EDE5" }} />;
  }
  return (
    <div style={{ height: 180 }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={{ lat, lng }}
        zoom={16}
        options={{
          disableDefaultUI: true,
          gestureHandling: "none",
          clickableIcons: false,
        }}
      >
        <Marker position={{ lat, lng }} title={name} />
      </GoogleMap>
    </div>
  );
};

export default HubScreen;
